import { addHours, addMinutes } from 'date-fns';
import { CreateParams, DeleteParams, DataProvider, GetListParams, GetOneParams, UpdateParams, 
  Identifier, startOptimisticMode, stopOptimisticMode, DeleteResult, GetManyParams} from 'ra-core'
import { AnyAction, Dispatch } from 'redux';
import { BackupResponseDto, CreateDatabaseInput, DatabaseResponseDto, EngineResponseDto, EnumOptionResponseDto, UpdateDatabaseInput, ValidationScheduleResponseDto } from '~/gen/api/backup';
import { CreateNetworkConnectionInput, CreateNetworkGatewayInput, DeploymentResponseDto, DeploymentStatus, NetworkConnectionResponseDto, NetworkGatewayResponseDto, NetworkGatewayStatus } from '~/gen/api/deploy';
import { EditObfuscationMappingInput, FieldObfsMappingDto, ScheduleEncryptionInput, DatabaseResponseDto as EDatabaseResponseDto } from '~/gen/api/encrypt';
import { BulkUpdateFieldGovernanceInput, BulkUpdateSelectedInput, DataAccessRoleResponseDto, DatabaseMappingCountsResponseDto, DataOwnerGroupResponseDto, EditEncryptionMappingInput } from '~/gen/api/governance';
import { LoginInput, ChangePasswordInput, ChangePhoneInput, ConfirmChangePhoneInput, InviteUserInput,
  ResetPasswordInput, RoleResponseDto, SsoConfigResponseDto, UpdateUserInput, UserInvitationResponseDto, UserResponseDto, EmailType,
  ClientStatus, UserOptionResponseDto} from '~/gen/api/user';
import { backupApiClient, clientApiClient, cloudSubscriptApiClient, dataAccessRoleApiClient, databasesApiClient, dataOwnerGroupApiClient, deployApiClient, deviceApiClient, encryptApiClient, encryptKeyApiClient, 
  fieldsApiClient, fileApiClient, fileEncryptApiClient, gatewayApiClient, govncDashboardApiClient, govrnOptionsApiClient, mappingsApiClient, metricsApiClient, networkConnApiClient, notiAssignApiClient, 
  obfsApiClient, optionsApiClient, organizationApiClient, reasonCollectApiClient, retentionRuleApiClient, ssoApiClient, subClsApiClient, subsetApiClient, tagsApiClient, userApiClient, secureFileApiClient } from '~/lib/client';
import retrieveAxiosErr from '~/utils/retrieveAxiosErr';
import { Adapter, AdapterTable, catchErr, feedEmptyDeleteResp, feedEmptyResp, getOneResp, idAlias, injectSimulatedGetMany, injectSimulatedGetOne, injectSimulatedOptimisticDelete, 
  RecPatch, substitute, SupportedResources, wrapFileDownEffect } from './tools';

const users: Partial<Adapter> = {
  getOne: ({ id }) => userApiClient.apiUsersIdGet(id as number)
    .then(
      getOneResp<UserResponseDto, UserResponseDto & RecPatch>(
        idAlias('userId')), 
      catchErr),
  getList: ({ pagination, sort, filter }) => 
    userApiClient.apiUsersPost({...filter, page: pagination.page, limit: pagination.perPage})
      .then((resp) => {
        const patchId = idAlias<UserResponseDto, 'userId'>('userId')
        return {
          data: resp.data.items?.map(patchId) || [],
          total: resp.data.total || 0,
        }
      }, catchErr),
  delete: ({ id }) => userApiClient.apiUsersIdDelete(+id).then(feedEmptyDeleteResp, catchErr),
  update: ({ data }: UpdateParams<UpdateUserInput>) => 
    userApiClient.apiUsersIdPut(data.id || 0, data)
      .then(feedEmptyResp, catchErr),
}

let dispatch: Dispatch<AnyAction> = (() => {}) as any


const adapterTable: AdapterTable = {
  'settings/organization/user-manage': { ...users },
  'users-browse': {
    getList: ({ pagination, sort, filter }) => 
      userApiClient.apiUsersOptionsGet(pagination.page, pagination.perPage, filter?.term)
        .then(({data}) => {
          const patchId = idAlias<UserOptionResponseDto, 'userId'>('userId')
          return {
            data: data.items?.map(patchId) || [],
            total: data.total || 0,
          }
        }, catchErr),
  },

  'login': {
    create: ({ data: input }: CreateParams<LoginInput>) => 
      userApiClient.apiUsersLoginPost(input).then(({ data }) => ({
        data: { ...data, id: data.id || 0, }
      }), catchErr)
  },
  'login-preload': {
    create: ({ data: email }) => ssoApiClient.apiSsoRedirecturlPost({email}).then(({data}) => ({
      data: { url: data, id: 0 }
    }), catchErr),
  },

  'resetpassword': {
    create: ({ data: input }: CreateParams<ResetPasswordInput>) =>
      userApiClient.apiUsersResetpasswordPost(input)
        .then(feedEmptyResp, catchErr)
  },
  'resetpassword-validate': {
    getOne: () => Promise.resolve({ data: { id: 99 }}),
    create: ({ data: {cid, ...input} }) =>
      userApiClient.apiUsersResetpasswordValidatePost(input, {
        headers: { 'X-Correlation-ID': cid },
      }).then(({ data }) => ({ data: { ...data, id: -1 }, }), catchErr),
    update: ({ data: {cid, ...input} }) =>
      userApiClient.apiUsersResetpasswordConfirmPost(input, {
        headers: { 'X-Correlation-ID': cid },
      }).then(feedEmptyResp, catchErr)
  },
  'changepassword': {
    create: ({ data: input }: CreateParams<ChangePasswordInput>) =>
      userApiClient.apiUsersProfilePasswordPut(input)
        .then(feedEmptyResp, catchErr)
  },

  'phone-veri-code': {
    create: ({ data: input }: CreateParams<ChangePhoneInput>) => 
      userApiClient.apiUsersProfilePhonePut(input)
        .then(({ data }) => ({
          data: { ...data, id: data.verificationCodeId || 0 }
        }), catchErr),
    update: ({ data: input }: UpdateParams<ConfirmChangePhoneInput>) => 
      userApiClient.apiUsersProfilePhoneConfirmPost(input)
        .then(feedEmptyResp, catchErr),
  },

  'user-register-activate': {
    create: ({ data: {code, cid}}) => 
      userApiClient.apiUsersRegisterValidatePost({ code }, { 
        headers: { 'X-Correlation-ID': cid },
      }).then(({data}) => ({data: { ...data, id: -1 }}), catchErr),
    update: ({data: {cid, ...rest}}) => 
      userApiClient.apiUsersRegisterConfirmPost(rest, {
        headers: { 'X-Correlation-ID': cid },
      }).then(feedEmptyResp, catchErr),
  },
  'user-invitation': {
    getList: ({ pagination, filter }) => 
      userApiClient.apiUsersInviteSearchPost({...filter, page: pagination.page, limit: pagination.perPage})
        .then((resp) => {
          const patchId = idAlias<UserInvitationResponseDto, 'userInvitationId'>('userInvitationId')
          return {
            data: resp.data.items?.map(patchId) || [],
            total: resp.data.total || 0,
          }
        }, catchErr),
    create: ({ data }: CreateParams<InviteUserInput>) => 
      userApiClient.apiUsersInvitePost(data).then(feedEmptyResp, catchErr),
    delete: ({ id }) => userApiClient.apiUsersInviteIdDelete(+id).then(() => ({ data: { id }}), catchErr),
  },

  'user-invitation-validate': {
    getOne: () => Promise.resolve({ data: { id: 99 }}),
    create: ({ data: {code, cid} }) => 
      userApiClient.apiUsersInviteValidatePost({ code }, {
        headers: { 'X-Correlation-ID': cid },
      }).then((resp) => ({ data: { ...resp.data, id: -1 } }), catchErr),
    update: ({ data: { cid, ...rest } }) => 
      userApiClient.apiUsersInviteConfirmPost(rest, {
        headers: { 'X-Correlation-ID': cid },
      }).then(feedEmptyResp, catchErr),
  },

  profile: {
    getOne: ({ id }) => userApiClient.apiUsersProfileGet()
      .then(getOneResp<UserResponseDto, UserResponseDto & RecPatch>(
        idAlias('userId'), 
        //@ts-ignore
        substitute('id', 'edit')), catchErr),
    create: ({ data }) => userApiClient.apiUsersProfileNewPost(
      data.firstName,
      data.lastName,
      data.phone,
      data.phoneVerifyCode,
      data.language,
      data.timeZoneName,
      data.avatarFile,
      data.phoneVerifyCodeId,
      data.mfaWay)
      .then(() => ({
        data: { 
          id: 'edit',
          username: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          mfaWay: data.mfaWay,
          language: data.language,
          timeZoneName: data.timeZoneName,
          timeZoneOffset: data.timeZoneOffset ? +data.timeZoneOffset : 0,
        }
      }), catchErr),
    update: ({ data }) => userApiClient.apiUsersProfileUpdatePost(
      data.firstName,
      data.lastName,
      data.language,
      data.timezone?.name,
      data.mfaWay,
      data.avatarFile,
    ).then(feedEmptyResp, catchErr)
  },
  'sso-logout': {
    create: () => ssoApiClient.apiSsoLogouturlPost().then((resp) => ({ data: resp.data ? { id: 1, url: resp.data as string} : {id:-1} }), catchErr),
  },

  'timezone-options': {
    getList: () => optionsApiClient.apiOptionsGet('timezone').then(({ data }) => ({
      data: data.map(idAlias('name')).sort((a, b) => { return a.value !== b.value ? ((+(a.value ?? 0)) - (+(b.value ?? 0))) : ((a.name ?? '') < (b.name ?? '') ? -1 : 1)  }),
      total: data.length,
    }), catchErr)
  },

  'settings/organization/profile': {
    getOne: () => organizationApiClient.apiOrganizationsProfileGet().then(getOneResp(substitute('id', 'edit')), catchErr),
    create: ({ data }) => organizationApiClient.apiOrganizationsProfilePost({
      id: data.id,
      name: data.name,
      planId: data.planId,
      streetAddress: data.streetAddress,
      address2: data.address2,
      country: data.country,
      state: data.state,
      city: data.city,
      zipCode: data.zipCode,
      invoiceEmail: data.invoiceEmail,
    }).then(feedEmptyResp, catchErr),
    update: ({ data }) => organizationApiClient.apiOrganizationsProfilePost({
      id: data.id,
      name: data.name,
      planId: data.planId,
      streetAddress: data.streetAddress,
      address2: data.address2,
      country: data.country,
      state: data.state,
      city: data.city,
      zipCode: data.zipCode,
      invoiceEmail: data.invoiceEmail,
    }).then(feedEmptyResp, catchErr),
  },
  
  'org-roles': {
    getList: () => organizationApiClient.apiOrganizationsRolesGet()
      .then((resp) => {
        return {
          data: resp.data.map(x => ({...x, id: `${x.organizationId}-${x.roleId}`})),
          total: resp.data.length,
        }
      }, catchErr),
    getOne: ({id}) => organizationApiClient.apiOrganizationsRolesIdGet(+(id as string).split('-')[1]).then(({data}) => ({data: {...data, id: `${data.organizationId}-${data.roleId}`}}), catchErr),
    create: ({data}) => organizationApiClient.apiOrganizationsRolesPost(data).then(feedEmptyResp, catchErr),
    update: ({id, data}) => organizationApiClient.apiOrganizationsRolesIdPut(+(id as string).split('-')[1], data).then(feedEmptyResp, catchErr),
    delete: ({id}) => organizationApiClient.apiOrganizationsRolesIdDelete(+(id as string).split('-')[1]).then(feedEmptyDeleteResp, catchErr),
  },
  'org-roles-default': {
    getOne: ({id}) => organizationApiClient.apiOrganizationsRolesIdGet(+id, true).then(({data}) => ({data: idAlias<RoleResponseDto, 'roleId'>('roleId')(data)}), catchErr),
  },
  'org-roles-permits': {
    getList: () => organizationApiClient.apiOrganizationsPermissionsGet().then(({data}) => ({
      data: data as any, total: data.length,
    }), catchErr),
  },

  'settings-clients-driver-encrypttool': {
    getList: ({filter:{status}}) => clientApiClient.apiClientsDriversEncryptToolsGet(status ?? ClientStatus.PendingApproval).then(({data}) => ({
      data: data.map(idAlias('clientId')),
      total: data.length,
    }), catchErr),
    update: ({ id, data }) => {
      const action = 
        data?.status === ClientStatus.Enabled ? (
          data?.prevStatus === ClientStatus.PendingApproval
            ? clientApiClient.apiClientsClientIdApprovePut
            : clientApiClient.apiClientsClientIdResumePut) 
        : data?.status === ClientStatus.Rejected ? clientApiClient.apiClientsClientIdRejectPut
        : data?.status === ClientStatus.Suspend ? clientApiClient.apiClientsClientIdSuspendPut
        : data?.status === ClientStatus.Revoked ? clientApiClient.apiClientsClientIdRevokePut
        : clientApiClient.apiClientsClientIdPut // data.status === null, SaveEdit
      return action.apply(clientApiClient, [id+'' as any, data]).then(feedEmptyResp, catchErr)
    },
  },
  'settings-clients-securefile': {
    getList: ({filter:{status, term}, pagination:{page, perPage}, sort:{field, order}}) => 
      clientApiClient.apiClientsSecurefilesGet(status ?? ClientStatus.Enabled, term, page, perPage, field, order === 'ASC').then(({data}) => ({
        data: data.items!.map(idAlias('clientId')),
        total: data.total!,
      }), catchErr),
    update: ({ id, data }) => {
      const action = 
        data?.status === ClientStatus.Enabled 
        && data?.prevStatus === ClientStatus.Suspend ? clientApiClient.apiClientsClientIdResumePut
        : data?.status === ClientStatus.Suspend ? clientApiClient.apiClientsClientIdSuspendPut
        : data?.status === ClientStatus.Revoked ? clientApiClient.apiClientsClientIdRevokePut
        : clientApiClient.apiClientsClientIdPut // data.status === null, SaveEdit
      return action.apply(clientApiClient, [id+'' as any, data]).then(feedEmptyResp, catchErr)
    },
  },
  'settings-clients-securefile-register-code': {
    getList: ({pagination:{page,perPage}}) => clientApiClient.apiClientsRegisterCodesGet(page, perPage).then(({data}) => ({
      data: data.items!.map(idAlias('codeId')),
      total: data.total!,
    })),
    create: ({data}) => clientApiClient.apiClientsRegisterCodesPost(data).then(({data}) => ({
      data: {...data, id: data.codeId!}
    }), catchErr),
    delete: ({id}) => clientApiClient.apiClientsRegisterCodesCodeIdDeclareVoidPut(+id).then(feedEmptyDeleteResp, catchErr),
  },
  'settings-clients-securefile-config': {
    getOne: () => secureFileApiClient.apiFilesSettingGet().then(({data}) => ({data: {...data, id: 'edit'}}), catchErr),
    update: ({data}) => secureFileApiClient.apiFilesSettingPost(data).then(feedEmptyResp, catchErr),
  },
  'settings-clients-securefile-customkey-config': {
    getOne: () => secureFileApiClient.apiCustomkeyvaultsGet().then(({data}) => ({data: {...data, id: 'edit'}}), catchErr),
    update: ({data}) => secureFileApiClient.apiCustomkeyvaultsEnablePost(data).then(feedEmptyResp, catchErr),
    delete: () => secureFileApiClient.apiCustomkeyvaultsDisablePost().then(feedEmptyDeleteResp, catchErr),
  },
  'settings/organization/ip-whitelist': {
    getList: () => organizationApiClient.apiOrganizationsStoragefirewallGet().then(({data}) => ({
      data: data.map(x => ({ id: x, ip: x, isCidr: x.indexOf('/') > -1 })),
      total: data.length,
    }), catchErr),
    create: ({data}) => organizationApiClient.apiOrganizationsStoragefirewallPost(data).then(() => ({data: {id:data.ip, ...data}}), catchErr),
    delete: ({id}) => {
      const x = id as string
      return organizationApiClient.apiOrganizationsStoragefirewallDelete({ ip: x, isCidr: x.indexOf('/') > -1}).then(feedEmptyDeleteResp, catchErr)
    },
  },

  'settings/organization/sso-config': {
    getOne: ({ id }) => ssoApiClient.apiSsoConfigGet()
      .then(getOneResp<SsoConfigResponseDto, SsoConfigResponseDto & RecPatch>((data) => ({...data, id})), catchErr),
    update: ({ id, data, previousData }: UpdateParams<SsoConfigResponseDto & RecPatch>) =>
      ssoApiClient.apiSsoConfigPost(data.isEnabled, data.protocol, data.samlLoginUrl ?? undefined, 
        data.samlLogoutUrl ?? undefined, data.samlEntityId ?? undefined, data.samlCertificate, 
        data.oidcDomain ?? undefined, data.oidcClientId ?? undefined, data.oidcClientSecret ?? undefined, data.oidcLogoutUrl ?? undefined)
        .then(() => ({ data: { 
          ...previousData,
          ...({
            isEnabled: data.isEnabled,
            protocol: data.protocol,
            samlLoginUrl: data.samlLoginUrl,
            samlLogoutUrl: data.samlLogoutUrl,
            samlEntityId: data.samlEntityId,
            oidcDomain: data.oidcDomain,
            oidcClientId: data.oidcClientId,
            oidcClientSecret: data.oidcClientSecret,
            oidcLogoutUrl: data.oidcLogoutUrl,
          }),
          ...(data.samlCertificate && {
            samlCertificate: data.samlCertificate,
          }),
          id,
        }}), catchErr),
  },
  'settings/organization/cloud-subscription': {
    getOne: ({ id }) => cloudSubscriptApiClient.apiCloudsubscriptionsGet().then(getOneResp((data) => ({...data, id: 1 })), catchErr),
    update: ({ data }) => cloudSubscriptApiClient.apiCloudsubscriptionsPost(data).then(feedEmptyResp, catchErr),
    delete: () => cloudSubscriptApiClient.apiCloudsubscriptionsDisablePut().then(feedEmptyDeleteResp, catchErr),
  },
  'settings-saml-slo-cert': {
    getOne: ({ id }: GetOneParams) => ssoApiClient.apiSsoSamlSloSigningCertGet({responseType: 'blob'})
      .then(wrapFileDownEffect('ondata_slo.pem'))
      .then(feedEmptyResp, catchErr)
  },
  'settings-cloud-subscription-test': {
    getList: ({ filter: input }) => cloudSubscriptApiClient.apiCloudsubscriptionsTestazurecloudPost(input).then(({data}) => ({
      data: [{...data, id: 1}],
      total: 1,
    }), catchErr),
  },
  'settings-cloud-subscription-consent-url': {
    getList: ({filter:{tenantId, cloudProvider}}) => cloudSubscriptApiClient.apiCloudsubscriptionsAzureconsentGet(tenantId, cloudProvider).then(({data}) => ({
      data: [{ id: 1, url: data }],
      total: 1,
    }), catchErr),
  },
  'settings-client-force-refresh-key': {
    update: ({id}) => clientApiClient.apiClientsClientIdKeyRefreshPut(String(id)).then(feedEmptyResp, catchErr),
  },
  'settings-client-share-secret': {
    getOne: ({id}) => Promise.resolve({data:{ id }}),
    create: ({data}) => clientApiClient.apiClientsCreateWithShareSecretPost(data).then(({data}) => ({
      data: { id: data.clientId || '', ...data }
    }), catchErr),
    update: ({id, data}) => clientApiClient.apiClientsClientIdKeyResetShareSecretPost(`${id}`, data).then(({data}) => ({
      data: { id, ...data }
    }), catchErr),
  },

  'settings/notification': {
    getList: () => notiAssignApiClient.apiNotiAssignGet().then(({data}) => {
      return {
        data: [EmailType.GenericAlert, EmailType.BackupAlert, EmailType.BackupNotReceivedAlert, EmailType.ValidationAlert]
          .map(et => data.find(x => x.notificationType === et) || { notificationType: et }).map(idAlias('notificationType')),
        total: 4,
      }
    }, catchErr),
    update: ({id, data: {roles, users}}) =>
      roles
      ? notiAssignApiClient.apiNotiAssignNotificationTypeRolesPut(+id, roles).then(feedEmptyResp, catchErr)
      : notiAssignApiClient.apiNotiAssignNotificationTypeUsersPut(+id, users).then(feedEmptyResp, catchErr),
  },

  'settings/encryption/keys': {
    getList: () => encryptKeyApiClient.apiEncryptionkeysGet().then(({data}) => ({
      data: data.map(idAlias('encryptionKeyId')),
      total: data.length,
    }), catchErr),
    create: ({data}) => encryptKeyApiClient.apiEncryptionkeysPost(data).then(({data:id}) => {
      return { data: { id, encryptionKeyId: id, ...data } }
    }, catchErr),
    delete: ({id}) => encryptKeyApiClient.apiEncryptionkeysEncryptionKeyIdDelete(+id).then(feedEmptyDeleteResp, catchErr),
  },
  'settings-encrypt-key-assign': {
    getOne: ({id}) => encryptKeyApiClient.apiEncryptionkeysDatabasesDatabaseIdGet(+id).then(({data}) => ({
      data: { ...data, id },
    }), catchErr),
    update: ({id, data}) => encryptKeyApiClient.apiEncryptionkeysAssignPost(data).then(() => ({
      data: { ...data, id }
    }), catchErr),
  },
  'token-validation-keys': {
    getList: () => encryptApiClient.apiEncryptionsPublickeyOrganizationGet().then(({data}) => ({
      data: data.map((x) => ({...x, id: `${x.databaseId}-${x.validationKeyId}`})),
      total: data.length,
    }), catchErr),
    create: ({data: input}) => encryptApiClient.apiEncryptionsPublickeyPost(input).then(feedEmptyResp, catchErr),
    update: ({id, data}) => encryptApiClient.apiEncryptionsPublickeyPut(data).then(feedEmptyResp, catchErr),
    delete: ({id}) => {
      const [databaseId, validationKeyId] = (id as string).split('-')
      return encryptApiClient.apiEncryptionsValidationKeyIdPublickeyDatabaseIdDelete(+validationKeyId, +databaseId)
        .then(feedEmptyDeleteResp, catchErr)
    },
  },
  'settings/encryption/obfuscation-rule': {
    getOne: ({id}) => obfsApiClient.apiObfuscationRulesRuleIdGet(+id).then(({data}) => ({
      data: {...data, id},
    }), catchErr),
    getList: () => obfsApiClient.apiObfuscationRulesGet().then(({data}) => ({
      data: data.map(idAlias('ruleId')),
      total: data.length,
    }), catchErr),
    create: ({data}) => obfsApiClient.apiObfuscationRulesPost(data).then(({data:id}) => ({
      data: {...data, id, ruleId: id },
    }), catchErr),
    update: ({id, data}) => obfsApiClient.apiObfuscationRulesRuleIdPut(+id, data.suppressAlert, data).then(feedEmptyResp, catchErr),
    delete: ({id, previousData}) => obfsApiClient.apiObfuscationRulesRuleIdDelete(+id, previousData?.suppressAlert).then(feedEmptyDeleteResp, catchErr),
  },
  'settings-obfs-rule-upload': {
    create: ({data}) => obfsApiClient.apiObfuscationRulesUploadPost(
      data.ruleName,
      data.dataType,
      data.description,
      data.valuesFile,
      data.ruleId,
    ).then(({data}) => {
      const respObj = {data:{id:data.ruleId as any, ...data}}
      dispatch(injectSimulatedGetOne('settings/encryption/obfuscation-rule', respObj))
      return respObj
    }, catchErr),
  },
  'settings-select-databases': {
    getList: () => obfsApiClient.apiObfuscationRulesOptionsDatabasesGet().then(({data}) => ({
      data: data.map(idAlias('key')),
      total: data.length,
    }), catchErr),
  },
  'settings-select-tables': {
    getList: ({filter:{databaseId}}) => obfsApiClient.apiObfuscationRulesOptionsTablesGet(+databaseId).then(({data}) => ({
      data: data.map(idAlias('key')),
      total: data.length,
    }), catchErr),
  },
  'settings-select-fields': {
    getList: ({filter:{databaseId, tableId}}) =>
      obfsApiClient.apiObfuscationRulesOptionsFieldsGet(+databaseId, +tableId).then(({data}) => ({
        data: data.map(idAlias('fieldId')),
        total: data.length,
      }), catchErr),
  },
  'settings-obfs-rule-usage-brief': {
    getOne: ({id}) => obfsApiClient.apiObfuscationRulesRuleIdQueryusageGet(+id).then(({data}) => ({ data: {...data, id}}), catchErr),
  },

  'settings/governance/data-classification': {
    getList: ({filter:{includeRefCount,useCache}}) => subClsApiClient.apiSecondaryclassificationsGet(includeRefCount).then(({data}) => ({
      data: data.map(idAlias('secondaryClassificationId')),
      total: data.length,
      validUntil: useCache && addMinutes(new Date(), 5),
    }), catchErr),
    create: ({data}) => subClsApiClient.apiSecondaryclassificationsPost(data).then(({data:id}) => ({
      data: {...data, secondaryClassificationId: id, id }
    }), catchErr),
    update: ({data, id}) => subClsApiClient.apiSecondaryclassificationsIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({id}) => subClsApiClient.apiSecondaryclassificationsIdDelete(+id).then(() => dispatchDelete('settings/governance/data-classification', {data:{id}}), catchErr),
  },
  'settings/governance/data-access-role': {
    getOne: ({id}) => dataAccessRoleApiClient.apiDataaccessrolesIdGet(+id).then(getOneResp<DataAccessRoleResponseDto, DataAccessRoleResponseDto&RecPatch>(idAlias('roleId')), catchErr),
    getList: () => dataAccessRoleApiClient.apiDataaccessrolesGet().then(({data}) => ({
      data: data.map(idAlias('roleId')),
      total: data.length,
    }), catchErr),
    create: ({data}) => dataAccessRoleApiClient.apiDataaccessrolesPost(data).then(({data:id}) => ({
      data: {...data, roleId:id, id}
    }), catchErr),
    update: ({data, id}) => dataAccessRoleApiClient.apiDataaccessrolesIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({id}) => dataAccessRoleApiClient.apiDataaccessrolesIdDelete(+id).then(() => dispatchDelete('settings/governance/data-access-role', {data:{id}}), catchErr),
  },
  'settings/governance/data-owner-group': {
    getOne: ({id}) => dataOwnerGroupApiClient.apiDataownergroupsIdGet(+id).then(getOneResp<DataOwnerGroupResponseDto, DataOwnerGroupResponseDto&RecPatch>(idAlias('ownerGroupId')), catchErr),
    getList: () => dataOwnerGroupApiClient.apiDataownergroupsGet().then(({data}) => ({
      data: data.map(idAlias('ownerGroupId')),
      total: data.length,
    }), catchErr),
    create: ({data}) => dataOwnerGroupApiClient.apiDataownergroupsPost(data).then(({data:id}) => ({
      data: {...data, ownerGroupId: id, id}
    }), catchErr),
    update: ({data, id}) => dataOwnerGroupApiClient.apiDataownergroupsIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({id}) => dataOwnerGroupApiClient.apiDataownergroupsIdDelete(+id).then(() => dispatchDelete('settings/governance/data-owner-group', {data:{id}}), catchErr),
  },
  'settings-data-owner-group-assignment-brief': {
    getOne: ({id}) => dataOwnerGroupApiClient.apiDataownergroupsIdAssignmentBriefGet(+id).then(({data}) => ({ data: {...data, id }}), catchErr),
  },

  'sso-redirect': {
    getOne: ({ id }) => ssoApiClient.apiSsoRedirecturlPost({ email: id as string }).then(getOneResp(), catchErr),
  },
  'device-verify': {
    create: ({data: {usercode}}) => deviceApiClient.apiDeviceVerifyUsercodePost(usercode).then(() => ({
      data: { id: -1, usercode }
    }), catchErr)
  },
  'device-consent': {
    getOne: ({ id: usercode }) => deviceApiClient.apiDeviceConsentGet(usercode as string).then(({data}) => ({
      data: { id: usercode, ...data }
    }), catchErr),
    update: ({ id: usercode }) => deviceApiClient.apiDeviceConsentPost(usercode as string).then(feedEmptyResp, catchErr)
  },

  'backups-n-validations/dashboard': {
    getOne: () => backupApiClient.apiBackupDashboardGet().then(({data}) => ({ data:{...data, id: -1}}), catchErr),
  },

  'backups-n-validations/list': {
  },
  
  'databases': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
    getList: () => databasesApiClient.apiDatabasesGet().then(({ data }) => {
      const patchId = idAlias<DatabaseResponseDto, 'databaseId'>('databaseId')
      return {
        data: data.map(patchId),
        total: data.length,
      }
    }, catchErr),
    create: ({ data: input }: CreateParams<CreateDatabaseInput>) => 
      databasesApiClient.apiDatabasesPost(input).then(({ data }) => ({
        data: {
          ...input,
          id: data,
          databaseId: data,
        }
      }), catchErr),
    update: ({ id, data }: UpdateParams<UpdateDatabaseInput>) =>
      databasesApiClient.apiDatabasesDatabaseIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({ id }) => databasesApiClient.apiDatabasesDatabaseIdDelete(+id).then(feedEmptyDeleteResp, catchErr),
  },
  'database-clone': {
    create: ({ data: { srcDatabase, cloneInput } }) => databasesApiClient.apiDatabasesDatabaseIdClonePost(srcDatabase.databaseId, cloneInput).then(({ data }) => {
      const clonedDbId = data
      dispatch(injectSimulatedGetOne('databases', {
        data: { ...srcDatabase, id: clonedDbId, name: cloneInput.databaseName, engineId: cloneInput.engineId, description: cloneInput.description }
      }))
      return ({
        data: { ...cloneInput, id: clonedDbId }
      })
    }, catchErr),
  },
  'databases/:id/keys-policy': {
    getList: ({filter: {databaseId}}) => encryptApiClient.apiEncryptionsPublickeyDatabaseDatabaseIdGet(+databaseId).then(({data}) => ({
      data: data.map(x => ({...x, id:`${x.databaseId}-${x.validationKeyId}`})),
      total: data.length,
    }), catchErr),
  },
  'database-policy-override': {
    getOne: ({id}) => encryptApiClient.apiEncryptionsPublickeyIsoverrideGet(+id).then(({data:isOverridden}) => ({
      data: { id, isOverridden },
    }), catchErr),
    update: ({id}) => encryptApiClient.apiEncryptionsPublickeySwitchoverrideDatabaseIdPut(+id).then(feedEmptyResp, catchErr),
  },
  'database-types': {
    getList: () => databasesApiClient.apiDatabasesTypesGet().then(({ data }) => {
      const patchId = idAlias<EnumOptionResponseDto, 'key'>('key')
      return {
        data: data.map(patchId),
        total: data.length,
      }
    }, catchErr)
  },
  'database-engine-versions': {
    getList: ({ filter: { databaseType } }: GetListParams) => 
      backupApiClient.apiBackupEnginesGet(databaseType).then(({ data }) => {
        const patchId = idAlias<EngineResponseDto, 'id'>('id')
        return {
          data: data.map(patchId),
          total: data.length,
        }
      }, catchErr)
  },
  'backup-recs': {
    getList: ({ filter: {databaseId}, pagination }) => 
      backupApiClient.apiBackupDetailsGet(databaseId, pagination.page, pagination.perPage).then(({ data }) => {
        const patchId = idAlias<BackupResponseDto, 'backupId'>('backupId')
        return {
          data: (data.items || []).map(patchId),
          total: data.total || 0,
        }
      }, catchErr),
  },
  'backup-retention-days': {
    getList: () => backupApiClient.apiBackupRetentiondaysGet().then(({ data }) => ({
      data: data.map(idAlias('key')),
      total: data.length,
    }), catchErr)
  },
  'upload-config-file': {
    getOne: ({ id }: GetOneParams) => backupApiClient.apiBackupUploadconfigGet(+id, {responseType: 'blob'})
      .then(wrapFileDownEffect('BackupConfig.ini'))
      .then(feedEmptyResp, catchErr)
  },
  'download-config-file': {
    getList: ({ filter: {dbId, backupId} }: GetListParams) => backupApiClient.apiBackupDownloadGet(dbId, backupId, {responseType: 'blob'})
      .then(wrapFileDownEffect('DownloadConfig.ini'))
      .then(() => Promise.resolve({data:[],total:0}), catchErr)
  },
  'validation-schedule': {
    getOne: ({ id }) => databasesApiClient.apiDatabasesDatabaseIdValidationscheduleGet(+id)
      .then(getOneResp<ValidationScheduleResponseDto, ValidationScheduleResponseDto & RecPatch>(
        idAlias('databaseId')
      ), catchErr),
    update: ({ data, id }: UpdateParams<ValidationScheduleResponseDto>) => 
      databasesApiClient.apiDatabasesDatabaseIdValidationschedulePut(+id, data)
        .then(feedEmptyResp, catchErr)
  },
  'dbmetrics-dbsize': {
    getList: ({ filter: {dbId, timeRangeType, endTime}}) =>
      metricsApiClient.apiMetricsLinechartDbsizeGet(dbId, timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: dbId + '-' + timeRangeType}],
        total: 1,
      }), catchErr),
  },
  'dbmetrics-indexsize': {
    getList: ({ filter: {dbId, timeRangeType, endTime}}) =>
      metricsApiClient.apiMetricsLinechartIndexsizeGet(dbId, timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: dbId + '-' + timeRangeType}],
        total: 1,
      }), catchErr),
  },
  'dbmetrics-rowcount': {
    getList: ({ filter: {dbId, timeRangeType, endTime}}) =>
      metricsApiClient.apiMetricsLinechartRowcountGet(dbId, timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: dbId + '-' + timeRangeType}],
        total: 1,
      }), catchErr),
  },
  'backup-schedule-type': {
    getList: ({ filter: {databaseId} }) => databasesApiClient.apiDatabasesDatabaseIdBackuptypesGet(databaseId).then(({data}) => ({
      data: data.map(idAlias('backupType')),
      total: data.length,
    }), catchErr),
  },
  'backup-schedule': {
    getList: ({ filter: {databaseId} }) => databasesApiClient.apiDatabasesDatabaseIdBackupscheduleGet(databaseId).then(({data}) => ({
      data: data.map(x => ({...x, id: `${x.databaseId}-${x.backupType}`})),
      total: data.length,
    }), catchErr),
    create: ({ data }) => 
      databasesApiClient.apiDatabasesDatabaseIdBackupschedulePost(data.databaseId, data).then(feedEmptyResp, catchErr),
    update: ({ data }) => 
      databasesApiClient.apiDatabasesDatabaseIdBackupschedulePut(data.databaseId, data).then(feedEmptyResp, catchErr),
    delete: ({ id, previousData:{databaseId, backupType} }: {id:Identifier, previousData?:any}) => 
      databasesApiClient.apiDatabasesDatabaseIdBackupscheduleBackupTypeDelete(databaseId, backupType).then(feedEmptyDeleteResp, catchErr),
  },

  'encryption/dashboard': {
    getOne: () => encryptApiClient.apiEncryptionsDashboardGet().then(
      getOneResp(
        //@ts-ignore
        substitute('id', -1)), 
      catchErr),
  },
  'encryption/mappings': {
    getOne: ({id}) => fieldsApiClient.apiFieldsMappingsDatabasesDatabaseIdGet(+id).then(({data}) => ({
      data: { id, ...data}
    }), catchErr),
    getList: () => fieldsApiClient.apiFieldsMappingsGet().then(({ data }) => ({ 
      data: data.map(idAlias('databaseId')), 
      total: data.length,
    }), catchErr),
    create: ({ data }: CreateParams<EditEncryptionMappingInput>) => 
      fieldsApiClient.apiFieldsMappingsEditPut(data).then(() => {
        injectSimulatedGetMany('encryption/mappings', {
          data: (data.fieldMappings || []).map(idAlias('fieldId')),
        })
        return feedEmptyResp()
      }, catchErr),
    // update: ({ data }: UpdateParams<EditEncryptionMappingInput>) => 
    //   fieldsApiClient.apiFieldsMappingsUpdatePut(data).then(feedEmptyResp, catchErr),
  },
  'encryption/mappings/nosql': {
    // getOne: () =>
    create: ({ data }) => 
      fieldsApiClient.apiFieldsMappingsCollectionEditPut(data).then(feedEmptyResp, catchErr),
  },
  'encryption/list': {
    getList: () => encryptApiClient.apiEncryptionsJobsGet().then(({ data }) => ({
      data: data.map(idAlias('databaseId')),
      total: data.length,
    }), catchErr),
    create: ({ data }: CreateParams<ScheduleEncryptionInput>) => 
      encryptApiClient.apiEncryptionsPost(data).then(feedEmptyResp, catchErr),
  },
  'encryption/custom-rules': {},
  'tables': {
    getList: ({ filter: {dbId} }) => fieldsApiClient.apiFieldsMappingsDatabasesDatabaseIdTablesGet(dbId).then(({ data }) => ({
      data: data.map(idAlias('tableId')),
      total: data.length,
    }), catchErr),
  },
  'collections': {
    getList: ({ filter: {dbId} }) => fieldsApiClient.apiFieldsMappingsDatabasesDatabaseIdCollectionsGet(dbId).then(({ data }) => ({
      data: data.map(idAlias('collectionId')),
      total: data.length,
    }), catchErr),
    update: ({ data: { databaseId, collectionId, sampleJson }}) => 
      encryptApiClient.apiEncryptionsSchemaCollectionPut(databaseId, collectionId, sampleJson).then(feedEmptyResp, catchErr),
  },
  'pending-tables': {
    getList: ({ filter: {dbId} }) => fieldsApiClient.apiFieldsMappingsDatabasesDatabaseIdPendingtablesGet(dbId).then(({ data }) => ({
      data: data.map(idAlias('tableId')),
      total: data.length,
    }), catchErr),
  },
  'pending-collections': {
    getList: ({ filter: {dbId} }) => fieldsApiClient.apiFieldsMappingsDatabasesDatabaseIdPendingcollectionsGet(dbId).then(({ data }) => ({
      data: data.map(idAlias('collectionId')),
      total: data.length,
    }), catchErr),
  },
  'fields': {
    getList: ({ filter: {dbId, tableId}}) => fieldsApiClient
      .apiFieldsMappingsDatabasesDatabaseIdTablesTableIdFieldsGet(dbId, tableId).then(({ data }) => ({
        data: data.map(idAlias('fieldId')),
        total: data.length,
      }), catchErr),
  },
  'collection-properties': {
    getList: ({ filter: {dbId, tableId}}) => fieldsApiClient
      .apiFieldsMappingsDatabasesDatabaseIdCollectionsCollectionIdPropertiesGet(dbId, tableId).then(({ data }) => ({
        data: data.map(idAlias('propertyId')),
        total: data.length,
      }), catchErr),
  },
  'mapping-rules': {
    getList: ({ filter: {dbId}}) => mappingsApiClient.apiMappingsRulesGet(dbId).then(({ data }) => ({
      data: data.map(idAlias('id')),
      total: data.length,
      validUntil: addHours(new Date(), 6),
    }), catchErr)
  },
  'encryption-historic-recs': {
    getList: ({ filter: {databaseId} }) => encryptApiClient.apiEncryptionsDetailGet(+databaseId).then(({ data }) => ({
      data: data.map(idAlias('encryptionId')),
      total: data.length,
    }), catchErr),
  },
  'encryptable-backups': {
    getList: ({ filter: {databaseId}}) => backupApiClient.apiBackupEncryptableGet(databaseId).then(({ data }) => ({
      data: data.map(idAlias('backupId')),
      total: data.length,
    }), catchErr)
  },
  'mappings-update': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
    getList: () => fieldsApiClient.apiFieldsMappingsGet().then(({ data }) => ({
      data: data.filter(x => (x.pendingReviewCount || 0) > 0).map(idAlias('databaseId')),
      total: data.length
    }), catchErr),
  },
  'encryption-rec-file': {
    getList: ({ filter: {dbId, encryptionId}}) => 
      encryptApiClient.apiEncryptionsDownloadGet(dbId, encryptionId, {responseType: 'blob'})
      .then(wrapFileDownEffect('DownloadEncryptionConfig.ini'))
      .then(() => Promise.resolve({data: [],total: 0}), catchErr),
  },
  'db-fields-mapping-count': {
    getOne: ({ id }) => fieldsApiClient.apiFieldsMappingsDatabasesDatabaseIdCountsGet(+id)
      .then(
        getOneResp<DatabaseMappingCountsResponseDto, DatabaseMappingCountsResponseDto & RecPatch>(
          idAlias('databaseId')), 
        catchErr),
  },
  'encryption/obfs/mappings': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
    getList: () => obfsApiClient.apiObfuscationMappingsDatabasesGet().then(({data}) => ({
      data: data.map(idAlias('databaseId')),
      total: data.length,
    }), catchErr),
    create: ({data}: CreateParams<{toSave: EditObfuscationMappingInput, updatedFields: (FieldObfsMappingDto&RecPatch)[]}>) => 
      obfsApiClient.apiObfuscationMappingsEditPost(data.toSave).then(() => {
        dispatch(injectSimulatedGetMany('obfs-field', {
          data: data.updatedFields,
        }))
        return feedEmptyResp()
      }, catchErr),
  },
  'encryption/obfs/list': {
    getList: () => obfsApiClient.apiObfuscationJobsGet().then(({data}) => ({
      data: data.map(idAlias('databaseId')),
      total: data.length,
    }), catchErr),
  },
  'obfuscation-historic-recs': {
    getList: ({filter: {databaseId}}) => obfsApiClient.apiObfuscationDetailGet(+databaseId).then(({data}) => ({
      data: data.map(idAlias('encryptionId')),
      total: data.length,
    }))
  },
  'obfs-table': {
    getList: ({filter:{databaseId}}) => obfsApiClient.apiObfuscationMappingsDatabasesDatabaseIdTablesGet(+databaseId).then(({data}) => ({
      data: data.map(x => ({...x, id: `${x.databaseId}-${x.tableId}`})),
      total: data.length,
    }), catchErr),
  },
  'obfs-field': {
    getList: ({filter:{databaseId,tableId}}) => obfsApiClient.apiObfuscationMappingsDatabasesDatabaseIdTablesTableIdFieldsGet(+databaseId, +tableId).then(({data}) => ({
      data: data.map(x => ({...x, id: `${databaseId}-${tableId}-${x.fieldId}`})),
      total: data.length,
    }), catchErr),
  },
  'token-validation-key-regenerate': {
    create: ({data}) => encryptApiClient.apiEncryptionsPublickeyDatabaseDatabaseIdRegenerateManagedPost(data)
      .then(feedEmptyResp, catchErr),
  },
  'settings/encryption/tokens': {
    getOne: ({id}) => {
      const [databaseId, ct] = (id + '').split('|')
      return encryptApiClient.apiEncryptionsDataAccessTokensDatabaseIdGet(+databaseId, ct).then(({data}) => ({
        data: data ? { id: `${data.databaseId}|${data.createdTime}`, ...data} : { id: -1 }
      }), catchErr)
    },
    getList: ({filter: {databaseId, validTokens}, pagination: { page, perPage }}) => encryptApiClient.apiEncryptionsDataAccessTokensGet(databaseId, validTokens, page, perPage)
      .then(({data}) => ({
        data: (data.items || []).map((x) => ({...x, id: `${x.databaseId}|${x.createdTime}`})),
        total: data.total || 0,
      }), catchErr),
    create: ({data}) => encryptApiClient.apiEncryptionsDataAccessTokensPost(data)
      .then(({data}) => ({
        data: {...data, id: data.createdTime || '' }
      }), catchErr),
  },
  'databases/:id/encryption-setting-change': {
    update: ({id, data}) => {
      const requested =
        data.action === 'lock'     ? encryptApiClient.apiEncryptionsDatabasesDatabaseIdLockPost(+id)
      : data.action === 'unlock'   ? encryptApiClient.apiEncryptionsDatabasesDatabaseIdUnlockPost(+id)
      : data.action === 'rollback' ? encryptApiClient.apiEncryptionsDatabasesDatabaseIdRollbackPost(+id)
      : data.action === 'finalize' ? encryptApiClient.apiEncryptionsDatabasesDatabaseIdFinalizePost(+id)
      : null
      if (!requested) { throw new Error('not supported action') }
      return requested.then(feedEmptyResp, catchErr)
    },
    getOne: ({id}) => encryptApiClient.apiEncryptionsDatabasesDatabaseIdSettingchangesGet(+id)
      .then(getOneResp(substitute('id' as any, id)), catchErr),
  },
  'encrypted-dbs': {
    getList: () => encryptApiClient.apiEncryptionsDatabasesEncryptedGet().then(({data}) => ({
      data: data.map(idAlias('databaseId')),
      total: data.length,
    }), catchErr),
  },
  'db-esc-status': {
    getOne: ({id}) => encryptApiClient.apiEncryptionsDatabasesDatabaseIdGet(+id).then(getOneResp<EDatabaseResponseDto, EDatabaseResponseDto&RecPatch>(idAlias('databaseId')), catchErr),
  },
  'table-rename-hint': {
    getOne: ({id}) => encryptApiClient.apiEncryptionsDatabasesDatabaseIdRenameHintsGet(+id).then(({data}) => ({
      data: ({ id, ...data })
    }), catchErr),
    update: ({id, data}) => encryptApiClient.apiEncryptionsDatabasesDatabaseIdRenameHintsPost(+id, data).then(feedEmptyResp, catchErr),
  },
  'file-encrypt-template': {
    getOne: ({id}) => fileEncryptApiClient.apiFileEncryptionsTemplatesTemplateIdGet(+id).then(({data}) => ({
      data: { id: (data ? id : -1), ...data }
    }), catchErr),
    getList: () => fileEncryptApiClient.apiFileEncryptionsTemplatesGet().then(({data}) => ({
      data: data.map(idAlias('templateId')),
      total: data.length,
    }), catchErr),
    create: ({data}) => fileEncryptApiClient.apiFileEncryptionsTemplatesPost(
      data.name,
      data.kind,
      data.delimitingChar,
      data.escapeChar,
      data.templateFile,
      data.copyFrom,
      data.includeObfs
    ).then(({data: id}) => ({ data: { id, name: data.name, kind: data.kind } }), catchErr),
    delete: ({id}) => fileEncryptApiClient.apiFileEncryptionsTemplatesTemplateIdDelete(+id).then(feedEmptyDeleteResp, catchErr),
    update: ({id, data}) => fileEncryptApiClient.apiFileEncryptionsTemplatesTemplateIdMappingsPost(+id, data).then(feedEmptyResp, catchErr),
  },
  'copy-file-mappings': {
    update: ({id, data}) => fileEncryptApiClient.apiFileEncryptionsTemplatesTemplateIdMappingsCopyPost(+id, data).then(feedEmptyResp, catchErr),
  },
  'file-encrypt-rule-map': {
    getOne: () => fileEncryptApiClient.apiFileEncryptionsDatatypeEncryptionRulesGet().then(({data}) => ({
      data: { id: 1, map: data },
    }), catchErr),
  },
  'encryption/subsetting/mastertables': {
    getOne: ({id}) => subsetApiClient.apiSubsetMastertablesGet(+id).then(({data}) => ({
      data: { id, data }
    }), catchErr),
    create: ({data}) => subsetApiClient.apiSubsetMastertablesPost(data).then(feedEmptyResp, catchErr),
  },
  'subset-tablerefs': {
    getOne: ({id}) => subsetApiClient.apiSubsetTableswithrefsGet(+id).then(({data}) => ({
      data: ({ id, data }),
    }), catchErr),
  },
  'subset-database-default-limit': {
    create: ({data}) => subsetApiClient.apiSubsetLimitPost(data).then(feedEmptyResp, catchErr),
  },
  'copy-table-mappings': {
    update: ({id, data}) => fieldsApiClient.apiFieldsMappingsTableCopyDryPost(data).then(({data}) => ({
      data: {id, ...data},
    }), catchErr),
    create: ({data}) => fieldsApiClient.apiFieldsMappingsTableCopyPost(data).then(feedEmptyResp, catchErr),
  },

  'deployments/dashboard': {
    getOne: ({id}) => deployApiClient.apiDeploymentsDashboardGet().then(({data}) => ({data:{ id: 1, ...data }}), catchErr),
  },
  'deployments/list': {
    getList: () => deployApiClient.apiDeploymentsGet().then(({data}) => ({
      data: data.map(idAlias('deploymentId')),
      total: data.length,
    }), catchErr),
    getOne: ({id}) => deployApiClient.apiDeploymentsDeploymentIdGet(+id)
      .then(getOneResp<DeploymentResponseDto, DeploymentResponseDto & RecPatch>(
        idAlias('deploymentId')
      ), catchErr),
    create: ({ data }) => deployApiClient.apiDeploymentsPost(data).then(({data:created}) => ({
      data: {id: created, deploymentId: created, databaseId: data.databaseId, name: data.databaseName}
    }), catchErr),
    delete: ({id, previousData:{ifBackup, data}}: {id:Identifier,previousData?:any}) => deployApiClient.apiDeploymentsDeploymentIdDelete(+id, ifBackup).then(() => { 
      if (data) {
        dispatch(injectSimulatedGetOne('deployments/list', {
          data: {...data, deploymentStatus: DeploymentStatus.Deleting },
        }))
      }
      return feedEmptyDeleteResp()
    }, catchErr),
  },
  'deployment-start': {
    update: ({ id }) =>
      deployApiClient.apiDeploymentsStartPut({ deploymentId: +id, action: 'start' }).then(feedEmptyResp, catchErr),
  },
  'deployment-stop': {
    update: ({ id }) =>
      deployApiClient.apiDeploymentsStopPut({ deploymentId: +id, action: 'stop' }).then(feedEmptyResp, catchErr),
  },
  'settings/deployment/network-configuration': {
    getList: () => gatewayApiClient.apiNetworkgatewaysGet().then(({data}) => ({
      data: data.map(idAlias('networkGatewayId')),
      total: data.length,
    }), catchErr),
    getOne: ({id}) => gatewayApiClient.apiNetworkgatewaysNetworkGatewayIdGet(+id)
      .then(getOneResp<NetworkGatewayResponseDto, NetworkGatewayResponseDto & RecPatch>(
        idAlias('networkGatewayId')
      ), catchErr),
    create: ({ data }: CreateParams<CreateNetworkGatewayInput>) =>
      gatewayApiClient.apiNetworkgatewaysPost(data).then(({data:created}) => ({
        data: {id: created, networkGatewayId: created, status: NetworkGatewayStatus.InProgress, ...data }
      }), catchErr),
    update: ({ id, data }) => 
      gatewayApiClient.apiNetworkgatewaysNetworkGatewayIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({ id }) => gatewayApiClient.apiNetworkgatewaysNetworkGatewayIdDelete(+id).then(feedEmptyDeleteResp, catchErr),
  },
  'network-connections': {
    getList: () => networkConnApiClient.apiNetworkconnectionsGet().then(({data}) => ({
      data: data.map(idAlias('networkConnectionId')),
      total: data.length,
    }), catchErr),
    getOne: ({ id }) => networkConnApiClient.apiNetworkconnectionsNetworkConnectionIdGet(+id)
      .then(getOneResp<NetworkConnectionResponseDto, NetworkConnectionResponseDto & RecPatch>(
        idAlias('networkConnectionId')
      ), catchErr),
    create: ({ data }: CreateParams<CreateNetworkConnectionInput>) => 
      networkConnApiClient.apiNetworkconnectionsPost(data).then(({data:created}) => ({ 
        data: {id: created, networkConnectionId: created, networkGatewayId: data.gatewayId, ...data }
      }), catchErr),
    update: ({ id, data }) => 
      networkConnApiClient.apiNetworkconnectionsNetworkConnectionIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({ id }) => networkConnApiClient.apiNetworkconnectionsNetworkConnectionIdDelete(+id).then(feedEmptyDeleteResp, catchErr),
  },
  'network-connection-sku': {
    getList: () => optionsApiClient.apiOptionsGet('network_connection_sku').then(({ data }) => ({
      data: data.map(idAlias('name')),
      total: data.length,
    }), catchErr)
  },
  'host-options': {
    getList: ({ filter:{ databaseId, backupId, encryptionId, customizedOption, isProduction }}) =>
      deployApiClient.apiDeploymentsHostoptionsGet(databaseId, backupId, encryptionId, customizedOption, isProduction).then(({data}) => ({
        data: data.map(x => { (x as any).id = `${x.engineId}-${x.vmSize}`; return (x as any) }),
        total: data.length,
      }), catchErr),
  },
  'random-key': {
    getOne: ({ id }) => 
      deployApiClient.apiDeploymentsRandomkeyGet().then(getOneResp(key => ({ id, key } as any)), catchErr),
  },
  'deployments': {},
  'dbmetrics-maxquerytime': {
    getList: ({filter:{timeRangeType, endTime}}) => 
      metricsApiClient.apiMetricsLinechartMaxquerytimeGet(timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: timeRangeType + '-' + endTime}],
        total: 1,
      }), catchErr)
  },
  'dbmetrics-averagequerytime': {
    getList: ({filter:{timeRangeType, endTime}}) => 
      metricsApiClient.apiMetricsLinechartAvgquerytimeGet(timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: timeRangeType + '-' + endTime}],
        total: 1,
      }), catchErr)
  },
  'dbmetrics-queryerrors': {
    getOne: () => Promise.resolve({data:{
      id: -1,
      legend: ['Northwind', 'SalesDB201', 'MyDB108'],
      xaxis: [
        "2021-07-27T17:34:33Z",
        "2021-07-28T11:04:33Z",
        "2021-07-29T07:45:41Z",
        "2021-07-30T04:44:05Z",
        "2021-07-31T02:34:36Z",
        "2021-08-01T15:43:20Z",
        "2021-08-02T07:12:03Z",
        "2021-08-03T13:46:24Z",
        "2021-08-04T07:04:08Z"
      ],
      series: [
        { name: 'Northwind', data: [0, 13, 6, 1, 4, 0, 0, 2, 5] },
        { name: 'SalesDB201', data: [6, 3, 0, 0, 4, 2, 8, 0, 1] },
        { name: 'MyDB108', data: [11, 1, 3, 1, 0, 0, 2, 2, 1] },
      ],
    }})
  },
  'dbmetrics-vmcpu': {
    getList: ({filter:{timeRangeType, endTime, deploymentId}}) =>
      metricsApiClient.apiMetricsLinechartVmcpuGet(deploymentId, timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: deploymentId + '-' + timeRangeType + '-' + endTime}],
        total: 1,
      }), catchErr)
  },
  'dbmetrics-vmmemory': {
    getList: ({filter:{timeRangeType, endTime, deploymentId}}) =>
      metricsApiClient.apiMetricsLinechartVmavailablememoryGet(deploymentId, timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: deploymentId + '-' + timeRangeType + '-' + endTime}],
        total: 1,
      }), catchErr)
  },
  'dbmetrics-vmdisk': {
    getList: ({filter:{timeRangeType, endTime, deploymentId}}) =>
      metricsApiClient.apiMetricsLinechartVmdiskGet(deploymentId, timeRangeType, endTime).then(({data}) => ({
        data: [{...data, id: deploymentId + '-' + timeRangeType + '-' + endTime}],
        total: 1,
      }), catchErr)
  },
    
  'governance/dashboard': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
  },
  'governance/data-catalog': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
    getList: ({filter:input, pagination:{page, perPage:limit}}) => 
      fieldsApiClient.apiFieldsSearchPost(page, limit, input).then(({data}) => {
        dispatch(injectSimulatedGetOne('govrnc-catalog-classi-statics', {
          data: {...data.primaryClassificationStatistics, id: '1' },
        }))
        dispatch(injectSimulatedGetOne('govrnc-catalog-encrypt-statics', {
          data: {...data.encryptMappingStatusStatistics, id: '1' },
        }))
        return {
          data: (data.searchDataCatalogPaginationResult?.items || []).map(x => ({...x, id: `${x.databaseId}-${x.schemaName}-${x.tableId}-${x.fieldId}`})),
          total: data.searchDataCatalogPaginationResult?.total || 0,
        }
      }, catchErr),
    update: ({data}) => fieldsApiClient.apiFieldsDatacatalogPost(data).then(feedEmptyResp, catchErr),
  },
  'governance/data-ownership': {
    getList: () => dataOwnerGroupApiClient.apiDataownergroupsOverviewGet().then(({data}) => ({
      data: data.map(idAlias('databaseId')),
      total: data.length,
    }), catchErr),
  },
  'govrnc-data-catalog-bulk': {
    create: ({data}) => fieldsApiClient.apiFieldsBulkupdatePost(data).then(feedEmptyResp, catchErr),
  },
  'govrnc-vali-bulk': {
    create: ({data:{bulkEditInput, lockedIds}}: {data:{bulkEditInput:BulkUpdateFieldGovernanceInput, lockedIds:BulkUpdateSelectedInput[]}}) => {
      if (bulkEditInput.selected?.length === 0) {
        return Promise.resolve({ data: { id: 1, failed: lockedIds }})
      } else {
        return fieldsApiClient.apiFieldsBulkfailedidsPost(bulkEditInput).then(({data}) => ({
          data: { id: 1, failed: [...data, ...lockedIds] },
        }), catchErr)
      }
    },
  },
  'govrnc-catalog-export': {
    getList: ({filter:input}) => fieldsApiClient.apiFieldsExportPost(input)
      .then(wrapFileDownEffect('DataCatalog.csv'))
      .then(() => Promise.resolve({data:[{id:-1,count:input._count}],total:0}), 
        (err) => Promise.resolve({data:[{id:-1,count:input._count, errMsg: retrieveAxiosErr(err)}],total:0}))
  },
  'govrnc-catalog-classi-statics': {
    getOne: () => Promise.resolve({ data: {id: -1} }),
  },
  'govrnc-catalog-encrypt-statics': {
    getOne: () => Promise.resolve({ data: {id: -1} }),
  },
  'govrnc-databases': {
    getList: () => govrnOptionsApiClient.apiOptionsDatabasesGet().then(({data}) => ({
      data: data.map(idAlias('key')),
      total: data.length,
      validUntil: addMinutes(new Date(), 5),
    }), catchErr),
  },
  'govrnc-schemas': {
    getList: ({filter:{database}}) => govrnOptionsApiClient.apiOptionsDatabasesDatabaseIdGet(database).then(({data}) => ({
      data: data.map(x => ({ id: `${database}-${x}`, key: x, text: x })),
      total: data.length,
      validUntil: addMinutes(new Date(), 5),
    }), catchErr),
  },
  'govrnc-tables': {
    getList: ({filter:{database,schema}}) => govrnOptionsApiClient.apiOptionsDatabasesDatabaseIdSchemasSchemaGet(database, schema).then(({data}) => ({
      data: data.map(x => ({...x, id: `${database}-${schema}-${x.key}`})),
      total: data.length,
      validUntil: addMinutes(new Date(), 5),
    }), catchErr),
  },
  'govrnc-fields': {
    getList: ({filter:{database,table}}) => govrnOptionsApiClient.apiOptionsDatabasesDatabaseIdTablesTableIdGet(database, table).then(({data}) => ({
      data: data.map(x => ({...x, id: `${database}-${table}-${x.key}`})),
      total: data.length,
      validUntil: addMinutes(new Date(), 5),
    }), catchErr),
  },
  'govrnc-tags': {
    getList: ({filter:{useCache}}) => tagsApiClient.apiFieldtagsGet().then(({data}) => ({
      data: data.map(idAlias('tagId')),
      total: data.length,
      validUntil: useCache ? addMinutes(new Date(), 5) : undefined,
    }), catchErr),
    create: ({data}) => tagsApiClient.apiFieldtagsPost(data).then(({data:id}) => ({data:{...data, id, tagId: id}}), catchErr)
  },
  'govrnc-retention-rule': {
    getList: () => retentionRuleApiClient.apiRetentionruleGet().then(({data}) => ({
      data: data.map(idAlias('retentionRuleId')),
      total: data.length,
    })),
    create: ({data}) => retentionRuleApiClient.apiRetentionrulePost(data).then(({data:id}) => ({
      data: {...data, id, retentionRuleId: id}
    }), catchErr),
    update: ({id, data}) => retentionRuleApiClient.apiRetentionruleIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({id}) => retentionRuleApiClient.apiRetentionruleIdDelete(+id).then(() => dispatchDelete('govrnc-retention-rule', {data:{id}}), catchErr),
  },
  'govrnc-reason-collect': {
    getList: () => reasonCollectApiClient.apiReasoncollectGet().then(({data}) => ({
      data: data.map(idAlias('reasonCollectId')),
      total: data.length,
    })),
    create: ({data}) => reasonCollectApiClient.apiReasoncollectPost(data).then(({data:id}) => ({
      data: {...data, id, reasonCollectId: id}
    }), catchErr),
    update: ({id, data}) => reasonCollectApiClient.apiReasoncollectIdPut(+id, data).then(feedEmptyResp, catchErr),
    delete: ({id}) => reasonCollectApiClient.apiReasoncollectIdDelete(+id).then(() => dispatchDelete('govrnc-reason-collect', {data:{id}}), catchErr),
  },
  'govrnc-select-all-fields': {
    getList: ({filter}) => fieldsApiClient.apiFieldsBulkhelpPost(filter).then(({data}) => ({
      data: [{ id: 1, ids: data }],
      total: 1,
    }), catchErr),
  },
  'govrnc-encrypt-rule-mapping': {
    getList: () => fieldsApiClient.apiFieldsEncryptionruledatatypemappingGet().then(({data}) => ({
      data: [{ 
        id: 1, 
        map: data.reduce((acc, x) => {
          const key = `${x.databaseType}-${x.dataType}`
          const arr = acc[key] || (acc[key] = [])
          arr.push(x)
          return acc
        }, {} as any)
      }],
      total: 1,
      validUntil: addMinutes(new Date(), 4 * 60),
    }), catchErr)
  },
  'govrnc-data-ownership': {
    getList: ({filter:{databaseId}}) => dataOwnerGroupApiClient.apiDataownergroupsDatabaseDatabaseIdOwnershipGet(+databaseId).then(({data}) => ({
      data: data.map(d => ({...d, id: `${databaseId}-${d.tableId}`})).sort((a, b) => (a.tableName || '') < (b.tableName || '') ? -1 : 1),
      total: data.length,
    }), catchErr),
    create: ({data}) => dataOwnerGroupApiClient.apiDataownergroupsAssignPut(data).then(feedEmptyResp, catchErr),
  },
  'govrnc-dashboard': {
    getList: ({
      pagination: { page, perPage },
      sort: { field, order },
      filter: { term },
    }) => govncDashboardApiClient.apiGovncDashboardGet(page, perPage, field, (order || "").toLocaleLowerCase() === 'asc', term).then(({data}) => ({
      data: (data.items || []).map(idAlias('databaseId')),
      total: data.total || 0,
    }), catchErr),
  },
  'governance': {
    getList: () => Promise.resolve({
      data: [
        { id: 1, name: 'MyDB 108', size: '5.2 GB', lastSuccessfulBackup: '2/18/2021 4:23:45', newFieldsNum: '2', action: 'Review', },
        { id: 2, name: 'SalesDB 201', size: '14.6 GB', lastSuccessfulBackup: '2/18/2021 4:05:45', newFieldsNum: '4', action: 'Review', },
        { id: 3, name: 'LobDB 125', size: '4.3 GB', lastSuccessfulBackup: '2/18/2021 3:09:23', newFieldsNum: '3', action: 'Review', },
        { id: 4, name: 'HRDB 301', size: '1.8 GB', lastSuccessfulBackup: '2/18/2021 2:43:08', newFieldsNum: '6', action: 'Review', },
        { id: 5, name: 'UserDB 423', size: '28.4 GB', lastSuccessfulBackup: '2/18/2021 1:12:54', newFieldsNum: '12', action: 'Review', },
      ],
      total: 5,
    })
  },
  'databases-dictionary': {
    getList: () => Promise.resolve({
      data: [
        { id: 1, name: 'MyDB 108', },
        { id: 2, name: 'SalesDB 201', },
        { id: 3, name: 'LobDB 125', },
        { id: 4, name: 'HRDB 301', },
        { id: 5, name: 'UserDB 423', },
      ],
      total: 5,
    })
  },
  'secure-files/dashboard': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
  },
  'secure-files-dashboard': {
    getList: ({filter:searchInput, pagination:{page, perPage}, sort:{field, order}}) => 
      secureFileApiClient.apiFilesAuditSearchPost({...searchInput, pageIndex: page, pageSize: perPage, sortField: field, sortAscending: order === 'ASC'}).then(({data}) => ({
        data: data.items!.map(x => ({...x, id: `${x.instanceId}-${x.fileId}-${x.timeStamp}`})),
        total: data.total || 0,
    }), catchErr), 
  },
  'secure-files': {
    getOne: ({id}) => secureFileApiClient.apiFilesFileIdGet(+id, true).then(({data}) => ({data: {...data, id: data.fileId!}}), catchErr),
  },
  'secure-files/file-catalog': {
    getList: ({filter:searchInput, pagination}) => 
      secureFileApiClient.apiFilesSearchPost({...searchInput, pageIndex: pagination.page, pageSize: pagination.perPage}).then(({data}) => ({
        data: data.items!.map(x => ({...x, id: `${x.fileId}`})),
        total: data.total ?? 0,
      }), catchErr),
    update: ({id, data}) => secureFileApiClient.apiFilesFileIdPut(id as any, data).then(feedEmptyResp, catchErr),
  },
  'secure-files-tags': {
    getList: () => secureFileApiClient.apiFilesTagsGet().then(({data}) => ({
      data: data.map(idAlias('tagId')),
      total: data.length,
    })),
    create: ({data:input}) => secureFileApiClient.apiFilesTagsPost(input).then(({data}) => ({
      data: {...data, id: data.tagId!}
    }), catchErr)
  },
  'secure-files-instances': {
    getList: ({filter:{id, showDelete}, pagination}) => secureFileApiClient.apiFilesFileIdInstancesGet(id, pagination.perPage, pagination.page, !!showDelete).then(({data}) => ({
      data: data.items!.map(x => ({...x, id: `${x.fileId}-${x.instanceId}`})),
      total: data.total ?? 0,
    }), catchErr),
  },
  'secure-files/file-access-log': {
    getList: ({filter:input, pagination:{page,perPage}, sort:{field,order}}) => 
      secureFileApiClient.apiFilesAccessSearchPost({...input, pageIndex: page, pageSize: perPage, sortField: field, sortAscending: order === 'ASC'}).then(({data}) => ({
        data: data.items!.map(x => ({...x, id: `${x.fileId}-${x.instanceId}-${x.timeStamp}`})),
        total: data.total ?? 0,
      }), catchErr),
  },
  'performance-tuning/dashboard': {
    getOne: () => Promise.resolve({data: { 
      id: -1, 
      performanceSuccessCount: 3,
      performanceWarningCount: 1,
      performanceFailedCount: 1,
      performanceAlerts: [
        { 
          alert: 'Failed to apply all performance tuning options for the "SalesDB 201" database.', 
          isWarning: true,
          alertTime: 'Feb 18, 2021, 4:05:24' 
        },
        { 
          alert: 'Failed to analyze the performance for "HRDB 301" database.', 
          isWarning: true,
          alertTime: 'Feb 18, 2021, 2:43:08' 
        },
      ],
    }}),
  },
  'dbmetrics-querywaittime': {
    getList: ({ filter: {dbId, timeRangeType, endTime}}) =>
      Promise.resolve(({
        // data: [{...data, id: dbId + '-' + timeRangeType}],
        data: [{ 
          legend: ['Northwind', 'SalesDB201', 'MyDB108'],
          xaxis: [
            "2021-08-27T17:34:33Z",
            "2021-08-28T11:04:33Z",
            "2021-08-29T07:45:41Z",
            "2021-08-30T04:44:05Z",
            "2021-08-31T02:34:36Z",
            "2021-09-01T15:43:20Z",
            "2021-09-02T07:12:03Z",
            "2021-09-03T13:46:24Z",
            "2021-09-04T07:04:08Z"
          ],
          series: [
            { name: 'Northwind', data: [35, 67, 569, 47, 62, 22, 83, 270, 94], },
            { name: 'SalesDB201', data: [217, 351, 208, 604, 163, 961, 102, 409, 262], },
            { name: 'MyDB108', data: [8076, 5420, 8995, 6661, 9583, 6320, 3846, 1610, 765], },
          ],
          id: -1 
        }],
        total: 1,
      })),
  },
  'dbmetrics-timeoutnum': {
    getList: ({ filter: {dbId, timeRangeType, endTime}}) =>
      Promise.resolve(({
        // data: [{...data, id: dbId + '-' + timeRangeType}],
        data: [{ 
          legend: ['Northwind', 'SalesDB201', 'MyDB108'],
          xaxis: [
            "2021-08-27T17:34:33Z",
            "2021-08-28T11:04:33Z",
            "2021-08-29T07:45:41Z",
            "2021-08-30T04:44:05Z",
            "2021-08-31T02:34:36Z",
            "2021-09-01T15:43:20Z",
            "2021-09-02T07:12:03Z",
            "2021-09-03T13:46:24Z",
            "2021-09-04T07:04:08Z"
          ],
          series: [
            { name: 'Northwind', data: [0, 2, 5, 0, 1, 1, 3, 2, 0], },
            { name: 'SalesDB201', data: [2, 3, 16, 6, 6, 9, 3, 4, 2], },
            { name: 'MyDB108', data: [0, 1, 0, 0, 0, 4, 1, 2, 1], },
          ],
          id: -1 
        }],
        total: 1,
      })),
  },

  'downloadScripts': {
    getList: ({ filter:{resId:name, filename} }) => fileApiClient.apiFilesScriptsGet(name, {responseType: 'blob'})
      .then(wrapFileDownEffect(filename || 'download'))
      .then(() => Promise.resolve({data:[],total:0}), catchErr)
  },

  'noop': {
    getOne: () => Promise.resolve({ data: { id: -1 }}),
    getList: () => Promise.resolve({ data: [], total: 0}),
  },
}



function getOne(resource: SupportedResources, params: GetOneParams) {
  const workerFn = adapterTable[resource] && adapterTable[resource]['getOne']
  if (workerFn) {
    return workerFn(params)
  } else {
    return Promise.reject(new Error('DataProvider: request operation not supported'))
  }
}

function getList(resource: SupportedResources, params: GetListParams) {
  const workerFn = adapterTable[resource] && adapterTable[resource]['getList']
  if (workerFn) {
    return workerFn(params)
  } else {
    return Promise.reject(new Error('DataProvider: request operation not supported'))
  }
}

function getMany(resource: SupportedResources, params: GetManyParams) {
  const workerFn = adapterTable[resource] && adapterTable[resource]['getMany']
  if (workerFn) {
    return workerFn(params)
  } else {
    return Promise.reject(new Error('DataProvider: request operation not supported'))
  }
}

function create(resource: SupportedResources, params: CreateParams) {
  const workerFn = adapterTable[resource] && adapterTable[resource]['create']
  if (workerFn) {
    return workerFn(params)
  } else {
    return Promise.reject(new Error('DataProvider: request operation not supported'))
  }
}

function update(resource: SupportedResources, params: UpdateParams) {
  const workerFn = adapterTable[resource] && adapterTable[resource]['update']
  if (workerFn) {
    return workerFn(params)
  } else {
    return Promise.reject(new Error('DataProvider: request operation not supported'))
  }
}

function deleteFn(resource: SupportedResources, params: DeleteParams) {
  const workerFn = adapterTable[resource] && adapterTable[resource]['delete']
  if (workerFn) {
    return workerFn(params)
  } else {
    return Promise.reject(new Error('DataProvider: request operation not supported'))
  }
}


const setDispatcher = (x: Dispatch) => {
  if (x) {
    dispatch = x
  }
}

const dispatchDelete = (resource: SupportedResources, payload: DeleteResult) => {
  setTimeout(() => {
    dispatch(startOptimisticMode())
    dispatch(injectSimulatedOptimisticDelete(resource, payload))
    dispatch(stopOptimisticMode())
  })
  return feedEmptyDeleteResp(payload.data.id)
}


const dataProvider = {
  getOne,
  getList,
  getMany,
  create,
  update,
  'delete': deleteFn,
} as DataProvider

dataProvider.setDispatcher = setDispatcher

export default dataProvider;

